import { getJTI } from '@frontend/auth-helpers';
import appConfig from '@frontend/env';
import { sentry } from '@frontend/tracking';

const backendApi = appConfig.BACKEND_API;

export const authorizedFetch = <T extends Record<string, any>>(): ((
  url: string,
  reqInit: RequestInit
) => Promise<T>) => {
  const jti = getJTI();
  if (!jti) {
    const error = new Error('No JTI found');
    sentry.error({
      error,
      topic: 'authorized-fetch',
      team: 'users-infra',
    });
    throw error;
  }
  return baseFetch<T>((r) => {
    r.headers = {
      ...r.headers,
      Authorization: `Bearer ${jti}`,
    };
  });
};

export const baseFetch = <T extends Record<string, any>>(
  modifyReq?: (r: RequestInit) => void
): ((url: string, reqInit: RequestInit) => Promise<T>) => {
  return async (url: string, reqInit: RequestInit) => {
    if (modifyReq) {
      modifyReq(reqInit);
    }

    const res = await fetch(`${backendApi}${url}`, reqInit);
    const contentType = res.headers.get('content-type');
    let json: any;
    if (contentType && contentType.includes('application/json')) {
      json = await res.json();
    } else {
      json = await res.text();
    }
    if (!res.ok) {
      throw new Error(
        typeof json === 'object' && json !== null && 'message' in json
          ? json.message
          : `Request failed with status ${res.status} ${json}`
      );
    }

    return json as T;
  };
};

export const getHostFromURL = (inUrl: string): string | undefined => {
  try {
    // Parse the requestUrl using the URL class
    const url = new URL(inUrl);

    // Extract and return the protocol and host property
    return `${url.protocol}//${url.host}`;
  } catch (error) {
    console.error('Error parsing requestUrl:', error);
    return;
  }
};

export const getRedirectURL = (requestUrl: string): string | undefined => {
  try {
    // Parse the requestUrl using the URL class
    const url = new URL(requestUrl);

    // Extract the value of the 'redirect_uri' query parameter
    const redirectUriParam = url.searchParams.get('redirect_uri');

    // Return the decoded value of the parameter
    return redirectUriParam ? getHostFromURL(decodeURIComponent(redirectUriParam)) : undefined;
  } catch (error) {
    console.error('Error parsing requestUrl:', error);
    sentry.error({
      error: 'Error parsing requestUrl',
      topic: 'getRedirectURL',
      team: 'users-infra',
    });
    return;
  }
};
